import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import api from '../../services/api';
import { uploadFile } from '../../services/upload';
import { states, statesAndDistricts, getDistrictsByState } from '../../data/districts';
import './Dashboard.css';

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState({
    totalShops: 0,
    totalAgents: 0,
    activeAds: 0,
    totalCategories: 0
  });
  const [agents, setAgents] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showShopModal, setShowShopModal] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [registering, setRegistering] = useState(false);

  // Agent form
  const [agentForm, setAgentForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    address: '',
    district: ''
  });

  // Shop form
  const [shopForm, setShopForm] = useState({
    name: '',
    logoUrl: '',
    websiteUrl: '',
    latitude: '',
    longitude: '',
    district: '',
    state: '',
    brandGroupId: '',
    email: '',
    password: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [shopsRes, agentsRes, adsRes, categoriesRes] = await Promise.all([
        api.get('/api/super/shops?limit=5'),
        api.get('/api/super/agents?limit=5'),
        api.get('/api/super/ads?limit=1'),
        api.get('/api/super/categories')
      ]);

      setStats({
        totalShops: shopsRes.data.pagination?.total || 0,
        totalAgents: agentsRes.data.pagination?.total || 0,
        activeAds: adsRes.data.pagination?.total || 0,
        totalCategories: categoriesRes.data.data?.length || 0
      });
      setAgents(agentsRes.data.data || []);
      setShops(shopsRes.data.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterAgent = async (e) => {
    e.preventDefault();
    setRegistering(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.post('/api/super/agents/register', agentForm);
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Agent registered successfully!' });
        setShowAgentModal(false);
        setAgentForm({ name: '', email: '', password: '', phone: '', address: '', district: '' });
        fetchData();
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error registering agent'
      });
    } finally {
      setRegistering(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRegisterShop = async (e) => {
    e.preventDefault();
    setRegistering(true);
    setMessage({ type: '', text: '' });

    try {
      let finalFormData = { ...shopForm };

      // Upload logo if file is selected
      if (logoFile) {
        const uploadResult = await uploadFile(logoFile, 'logo');
        if (uploadResult.success) {
          finalFormData.logoUrl = uploadResult.url;
        } else {
          setMessage({ type: 'error', text: uploadResult.message || 'Error uploading logo' });
          setRegistering(false);
          return;
        }
      }

      const response = await api.post('/api/super/shops', finalFormData);
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Shop registered successfully!' });
        setShowShopModal(false);
        setShopForm({
          name: '',
          logoUrl: '',
          websiteUrl: '',
          latitude: '',
          longitude: '',
          district: '',
          state: '',
          brandGroupId: '',
          email: '',
          password: ''
        });
        setLogoFile(null);
        setLogoPreview('');
        fetchData();
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error registering shop'
      });
    } finally {
      setRegistering(false);
    }
  };

  const allDistricts = Object.values(statesAndDistricts).flat();

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="loading"><div className="spinner"></div></div>
      </div>
    );
  }

  return (
    <div className="app">
      <Navbar />
      <div className="container">
        <div className="dashboard-header">
          <div>
            <h1 className="page-title">Super Admin Dashboard</h1>
            <p className="page-subtitle">Manage agents, shops, and platform settings</p>
          </div>
          <div className="header-actions">
            <button
              className="btn btn-primary"
              onClick={() => setShowAgentModal(true)}
            >
              + Register Agent
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setShowShopModal(true)}
            >
              + Register Shop
            </button>
          </div>
        </div>

        {message.text && (
          <div className={`alert alert-${message.type}`} style={{ marginBottom: '20px' }}>
            {message.text}
          </div>
        )}

        <div className="stats-grid">
          <div className="stat-card stat-primary">
            <div className="stat-icon">👤</div>
            <div className="stat-content">
              <h3>Total Agents</h3>
              <div className="value">{stats.totalAgents}</div>
              <Link to="/super/agents" className="stat-link">View all →</Link>
            </div>
          </div>
          <div className="stat-card stat-success">
            <div className="stat-icon">🏪</div>
            <div className="stat-content">
              <h3>Total Shops</h3>
              <div className="value">{stats.totalShops}</div>
              <Link to="/super/shops" className="stat-link">View all →</Link>
            </div>
          </div>
          <div className="stat-card stat-info">
            <div className="stat-icon">📢</div>
            <div className="stat-content">
              <h3>Active Ads</h3>
              <div className="value">{stats.activeAds}</div>
              <Link to="/super/ads" className="stat-link">View all →</Link>
            </div>
          </div>
          <div className="stat-card stat-warning">
            <div className="stat-icon">📁</div>
            <div className="stat-content">
              <h3>Categories</h3>
              <div className="value">{stats.totalCategories}</div>
              <Link to="/super/categories" className="stat-link">View all →</Link>
            </div>
          </div>
        </div>

        <div className="dashboard-grid">
          <div className="dashboard-card">
            <div className="card-header">
              <h2>Recent Agents</h2>
              <Link to="/super/agents" className="view-all-link">View All</Link>
            </div>
            <div className="card-list-container">
              {agents.length === 0 ? (
                <div className="empty-state">
                  <p>No agents registered yet. Click "Register Agent" to get started.</p>
                </div>
              ) : (
                <div className="card-list">
                  {agents.map((agent) => (
                    <div key={agent._id} className="list-item-card">
                      <div className="list-item-header">
                        <div className="list-item-icon">👤</div>
                        <div className="list-item-content">
                          <h3 className="list-item-title">{agent.name || 'N/A'}</h3>
                          <p className="list-item-subtitle">{agent.email}</p>
                        </div>
                        <div className="list-item-badge">
                          <span className="badge badge-info">{agent.shopCount || 0} Shops</span>
                        </div>
                      </div>
                      <div className="list-item-details">
                        <div className="list-item-detail-item">
                          <span className="detail-label">Phone:</span>
                          <span className="detail-value">{agent.phone || 'N/A'}</span>
                        </div>
                        {agent.district && (
                          <div className="list-item-detail-item">
                            <span className="detail-label">District:</span>
                            <span className="detail-value">{agent.district}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="dashboard-card">
            <div className="card-header">
              <h2>Recent Shops</h2>
              <Link to="/super/shops" className="view-all-link">View All</Link>
            </div>
            <div className="card-list-container">
              {shops.length === 0 ? (
                <div className="empty-state">
                  <p>No shops registered yet. Click "Register Shop" to get started.</p>
                </div>
              ) : (
                <div className="card-list">
                  {shops.map((shop) => (
                    <div key={shop._id} className="list-item-card">
                      <div className="list-item-header">
                        <div className="list-item-logo">
                          {shop.logoUrl ? (
                            <img src={shop.logoUrl} alt={shop.name} />
                          ) : (
                            <div className="list-item-icon">🏪</div>
                          )}
                        </div>
                        <div className="list-item-content">
                          <h3 className="list-item-title">{shop.name}</h3>
                          <p className="list-item-subtitle">{shop.district}, {shop.state}</p>
                        </div>
                        <div className="list-item-badge">
                          <span className={`badge ${shop.isActive ? 'badge-success' : 'badge-danger'}`}>
                            {shop.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Agent Registration Modal */}
      {showAgentModal && (
        <div className="modal-overlay" onClick={() => setShowAgentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Register New Agent</h2>
              <button className="modal-close" onClick={() => setShowAgentModal(false)}>×</button>
            </div>
            <form onSubmit={handleRegisterAgent} className="modal-form">
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={agentForm.name}
                  onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={agentForm.email}
                  onChange={(e) => setAgentForm({ ...agentForm, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  value={agentForm.phone}
                  onChange={(e) => setAgentForm({ ...agentForm, phone: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>District *</label>
                <select
                  value={agentForm.district}
                  onChange={(e) => setAgentForm({ ...agentForm, district: e.target.value })}
                  required
                >
                  <option value="">Select District</option>
                  {Object.entries(statesAndDistricts).map(([state, districts]) => (
                    districts.map(district => (
                      <option key={`${state}-${district}`} value={district}>{district}, {state}</option>
                    ))
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Address</label>
                <textarea
                  value={agentForm.address}
                  onChange={(e) => setAgentForm({ ...agentForm, address: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={agentForm.password}
                  onChange={(e) => setAgentForm({ ...agentForm, password: e.target.value })}
                  required
                  minLength={6}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAgentModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={registering}>
                  {registering ? 'Registering...' : 'Register Agent'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Shop Registration Modal */}
      {showShopModal && (
        <div className="modal-overlay" onClick={() => {
          setShowShopModal(false);
          setLogoFile(null);
          setLogoPreview('');
        }}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Register New Shop</h2>
              <button className="modal-close" onClick={() => {
                setShowShopModal(false);
                setLogoFile(null);
                setLogoPreview('');
              }}>×</button>
            </div>
            <form onSubmit={handleRegisterShop} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Shop Name *</label>
                  <input
                    type="text"
                    value={shopForm.name}
                    onChange={(e) => setShopForm({ ...shopForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>State *</label>
                  <select
                    value={shopForm.state}
                    onChange={(e) => {
                      setShopForm({ ...shopForm, state: e.target.value, district: '' });
                    }}
                    required
                  >
                    <option value="">Select State</option>
                    {states.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>District *</label>
                  <select
                    value={shopForm.district}
                    onChange={(e) => setShopForm({ ...shopForm, district: e.target.value })}
                    required
                    disabled={!shopForm.state}
                  >
                    <option value="">Select District</option>
                    {shopForm.state && getDistrictsByState(shopForm.state).map(district => (
                      <option key={district} value={district}>{district}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Brand Group ID</label>
                  <input
                    type="text"
                    value={shopForm.brandGroupId}
                    onChange={(e) => setShopForm({ ...shopForm, brandGroupId: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Latitude *</label>
                  <input
                    type="number"
                    step="any"
                    value={shopForm.latitude}
                    onChange={(e) => setShopForm({ ...shopForm, latitude: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Longitude *</label>
                  <input
                    type="number"
                    step="any"
                    value={shopForm.longitude}
                    onChange={(e) => setShopForm({ ...shopForm, longitude: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Shop Logo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  style={{ marginBottom: '10px' }}
                />
                {logoPreview && (
                  <div style={{ marginTop: '10px' }}>
                    <img 
                      src={logoPreview} 
                      alt="Logo preview" 
                      style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '4px' }}
                    />
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Website URL</label>
                <input
                  type="url"
                  value={shopForm.websiteUrl}
                  onChange={(e) => setShopForm({ ...shopForm, websiteUrl: e.target.value })}
                />
              </div>
              <div className="form-divider">
                <span>Admin Account</span>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Admin Email *</label>
                  <input
                    type="email"
                    value={shopForm.email}
                    onChange={(e) => setShopForm({ ...shopForm, email: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Admin Password *</label>
                  <input
                    type="password"
                    value={shopForm.password}
                    onChange={(e) => setShopForm({ ...shopForm, password: e.target.value })}
                    required
                    minLength={6}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowShopModal(false);
                  setLogoFile(null);
                  setLogoPreview('');
                }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={registering}>
                  {registering ? 'Registering...' : 'Register Shop'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
